import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Server, Users, Award, Briefcase, HelpCircle, 
  FileText, Mail, Phone, MapPin, ArrowLeft, CheckCircle2, ChevronRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Grid3D, ParticleField, GlowOrb, GlowingCard, TiltCard, FloatingElement 
} from '../components/3DEffects';

export default function InfoPage() {
  const { pathname } = useLocation();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  // Determine page content based on URL path
  let title = '';
  let subtitle = '';
  let content = null;

  if (pathname === '/about') {
    title = 'About NetEye';
    subtitle = 'Securing and maintaining enterprise-grade IT infrastructure since 2011.';
    content = (
      <div className="space-y-16">
        {/* Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Our Story</h2>
            <p className="text-gray-400 leading-relaxed">
              NetEye started as a dedicated group of IT professionals committed to solving critical infrastructure challenges. Over the years, we have grown into a premier provider of Annual Maintenance Contracts (AMC) and enterprise-grade support for IT systems, CCTV networks, security systems, and custom software.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Today, we manage operations for over 500+ businesses globally, offering proactive 24/7 monitoring, certified hardware diagnostics, and rapid emergency site visits to minimize downtime.
            </p>
          </div>
          <div className="relative">
            <TiltCard intensity={5}>
              <div className="rounded-2xl overflow-hidden border border-blue-500/20 shadow-2xl relative">
                <img 
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80" 
                  alt="Server Infrastructure" 
                  className="w-full h-80 object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080B14] via-transparent to-transparent" />
              </div>
            </TiltCard>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-8">
          <GlowingCard className="p-8 border border-blue-500/10">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-400" /> Our Mission
            </h3>
            <p className="text-gray-400 leading-relaxed">
              To deliver seamless, proactive, and resilient IT infrastructure management solutions that empower businesses to focus on growth, knowing their critical assets are secure and perfectly maintained.
            </p>
          </GlowingCard>

          <GlowingCard className="p-8 border border-blue-500/10">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <Server className="w-6 h-6 text-cyan-400" /> Our Vision
            </h3>
            <p className="text-gray-400 leading-relaxed">
              To define the next generation of smart infrastructure support by combining human expertise with predictive AI diagnostics and 24/7 automated monitoring protocols.
            </p>
          </GlowingCard>
        </div>

        {/* Values */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-white text-center">Core Pillars</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Customer First', desc: 'Dedicated account managers and SLA response guarantees.' },
              { icon: Shield, title: 'Absolute Integrity', desc: 'Secure diagnostics, verified staff, and transparent quotes.' },
              { icon: Award, title: 'Certified Expertise', desc: 'Our technicians carry Cisco, Microsoft, and Dahua certifications.' },
              { icon: Server, title: 'Predictive Monitoring', desc: 'Proactive detection of issues before they impact your business.' }
            ].map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div key={idx} className="p-6 rounded-xl bg-white/5 border border-blue-500/5 hover:border-blue-500/25 transition-all text-center">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4 border border-blue-500/15 text-blue-400">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h4 className="text-white font-semibold mb-2">{pillar.title}</h4>
                  <p className="text-gray-500 text-sm">{pillar.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  } else if (pathname === '/careers') {
    title = 'Careers at NetEye';
    subtitle = 'Build the future of IT infrastructure management with a global team.';
    content = (
      <div className="space-y-16">
        {/* Culture intro */}
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-2xl font-bold text-white">Why Join NetEye?</h2>
          <p className="text-gray-400 leading-relaxed">
            We value creativity, technical execution, and ownership. Our teams build secure network tunnels, deploy advanced thermal CCTV arrays, develop SaaS dashboards, and maintain hardware across diverse business sectors.
          </p>
        </div>

        {/* Job postings */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
            <Briefcase className="w-6 h-6 text-blue-400" /> Open Positions
          </h2>
          <div className="space-y-4">
            {[
              { title: 'L2 Network Engineer', dept: 'Infrastructure & Routing', type: 'Full-time', loc: 'Remote / Purulia' },
              { title: 'CCTV Installation & Maintenance Lead', dept: 'Security Systems', type: 'Full-time', loc: 'West Bengal (Field)' },
              { title: 'Full-Stack Developer (Node/React)', dept: 'SaaS Platform Development', type: 'Full-time', loc: 'Remote' },
              { title: 'Enterprise Support Representative', dept: 'Customer Operations', type: 'Full-time', loc: 'Purulia Office' }
            ].map((job, idx) => (
              <GlowingCard key={idx} className="p-6 border border-blue-500/10 hover:border-blue-500/25 transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{job.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{job.dept}</span>
                      <span>•</span>
                      <span>{job.loc}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {job.type}
                    </span>
                    <Button variant="outline" className="border-blue-500/20 text-white hover:bg-blue-500/10">
                      Apply Now <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (pathname === '/help') {
    title = 'Help & Support Center';
    subtitle = 'Frequently asked questions, guides, and direct support lines.';
    content = (
      <div className="space-y-16">
        {/* Support Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-xl bg-white/5 border border-blue-500/5 text-center">
            <Mail className="w-8 h-8 text-blue-400 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Email Support</h3>
            <p className="text-gray-500 text-sm mb-4">Send us an inquiry directly</p>
            <a href="mailto:service.support@neteye.qzz.io" className="text-blue-400 text-sm font-semibold hover:underline">
              service.support@neteye.qzz.io
            </a>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-blue-500/5 text-center">
            <Phone className="w-8 h-8 text-blue-400 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Call Center</h3>
            <p className="text-gray-500 text-sm mb-4">24/7 client emergency lines</p>
            <span className="text-white text-sm font-semibold block">8436661595</span>
            <span className="text-white text-xs text-gray-500">7384331647 / 8617720844</span>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-blue-500/5 text-center">
            <MapPin className="w-8 h-8 text-blue-400 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Visit Office</h3>
            <p className="text-gray-500 text-sm mb-4">Walk-in consultation</p>
            <span className="text-gray-400 text-xs leading-relaxed">
              Dulmi, Nadiha, Purulia, West Bengal, 723101
            </span>
          </div>
        </div>

        {/* FAQs */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-blue-400" /> FAQ
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { q: 'What services are covered under the AMC?', a: 'Our Annual Maintenance Contracts cover hardware checkups, cleaning, wiring audits, software updates, emergency backup recovery, and priority on-site support visits depending on your chosen plan.' },
              { q: 'What is your response time for emergency visits?', a: 'For Diamond Plan users, we arrive on site within 2-4 hours. Gold Plan users receive visits within 24 hours, and Silver Plan users within 1-2 days.' },
              { q: 'How do I submit a technical support ticket?', a: 'Once logged in to your NetEye account, go to your Dashboard and click "Create Ticket". You can chat directly with our staff inside the ticket panel.' },
              { q: 'Can I cancel or change my contract?', a: 'Yes. Monthly billing contracts can be canceled or upgraded at any time from your billing dashboard. Yearly contracts receive a discount and are bound to a 12-month period.' }
            ].map((faq, idx) => (
              <GlowingCard key={idx} className="p-6 border border-blue-500/10">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
              </GlowingCard>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (pathname === '/terms') {
    title = 'Terms of Service';
    subtitle = 'Please read our terms of service agreement carefully.';
    content = (
      <div className="glass-premium p-8 md:p-12 rounded-2xl border border-blue-500/15 space-y-8 text-gray-400 leading-relaxed text-sm">
        <div className="flex items-center gap-3 border-b border-blue-500/10 pb-4 mb-6">
          <FileText className="w-6 h-6 text-blue-400" />
          <span className="text-white font-bold text-lg">Terms & Conditions</span>
        </div>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">1. Acceptance of Terms</h3>
          <p>
            By accessing and using the NetEye portal (the "Service"), you agree to comply with and be bound by these Terms of Service. If you do not agree, please do not use the Service.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">2. Scope of Services</h3>
          <p>
            NetEye provides IT infrastructure support, network engineering, CCTV camera installation, and software maintenance services under specified Annual Maintenance Contracts (AMC).
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">3. Account Registration & Security</h3>
          <p>
            You must register a valid user account (via Google OAuth or email) to access the tickets, billing, and bookings portals. You are responsible for keeping your session tokens secure and confidential.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">4. Service SLA & Limits</h3>
          <p>
            Service Level Agreements (SLAs) for technical visits vary by package tier (Silver, Gold, Diamond). NetEye is not responsible for delays caused by hardware supply chain issues, severe weather events, or unauthorized tampering by third-party technicians.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">5. Termination</h3>
          <p>
            We reserves the right to suspend or terminate accounts in violation of safety guidelines, illegal activity, or failure to process contract payments within 15 days of the billing invoice date.
          </p>
        </section>
      </div>
    );
  } else if (pathname === '/privacy') {
    title = 'Privacy Policy';
    subtitle = 'How we collect, protect, and use your personal information.';
    content = (
      <div className="glass-premium p-8 md:p-12 rounded-2xl border border-blue-500/15 space-y-8 text-gray-400 leading-relaxed text-sm">
        <div className="flex items-center gap-3 border-b border-blue-500/10 pb-4 mb-6">
          <Shield className="w-6 h-6 text-blue-400" />
          <span className="text-white font-bold text-lg">Data Privacy Guidelines</span>
        </div>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">1. Information We Collect</h3>
          <p>
            We collect personal details (name, email address, company profile, phone number) when you register via Google Auth or submit service inquiries. We also maintain records of support tickets, messages, bookings, and transaction status logs.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">2. How We Use Information</h3>
          <p>
            Your information is used to manage service schedules, fulfill ticketing requests, process Stripe payments, customize diagnostic reports, and verify admin access levels.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">3. Security and Storage</h3>
          <p>
            We encrypt session data, connection strings, and user profiles in a secure MongoDB Atlas cluster. No payment details (credit card info) are stored on our servers; they are processed entirely through Stripe's certified API endpoints.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-white font-semibold text-base">4. Third-Party Sharing</h3>
          <p>
            NetEye does not sell, rent, or trade your user data. We only share details with certified service providers essential for application functionality (Google OAuth, Stripe, and OpenAI).
          </p>
        </section>
      </div>
    );
  } else {
    title = 'Page Not Found';
    subtitle = 'The page you are looking for does not exist.';
    content = (
      <div className="text-center py-12">
        <Link to="/">
          <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 border-none font-semibold text-white">
            Return to Homepage
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 relative overflow-hidden" style={{ background: '#080B14' }}>
      {/* Background patterns */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Grid3D />
        <ParticleField count={25} />
        <GlowOrb color="royal" size={600} className="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <GlowOrb color="midnight" size={500} className="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm font-medium mb-12">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Page Header */}
        <div className="border-b border-blue-500/10 pb-8 mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {title}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            {subtitle}
          </p>
        </div>

        {/* Page Content */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {content}
        </motion.div>
      </div>
    </div>
  );
}
